import { t } from "i18next";
import { STATUSFIELD_VALUE_LABEL } from "../pages/ISSB/IssbDataInput/components/AdditionalStatuses";
import { marked } from 'marked';


export function estimateRowsForTextarea(text, minRows = 3, characterWidth = 80) {
  if (!text) return minRows;
  if (typeof text !== "string") {
    console.error("(swallow) estimateRowsForTextarea: text is not a string?!", text);
    return minRows;
  }
  let lines = text.split("\n");
  let rowCnt = 0;
  lines.forEach((line) => {
    rowCnt += Math.ceil(line.length / characterWidth);
  });
  return Math.max(minRows, rowCnt);
}

/**
 * HACK possibly a csv string or an array
 * @param {string|string[]|number} value
 * @returns {object[]}
 */
export function toArray(value) {
  if (value === null || value === undefined || value === "") return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") return splitCSV(value);
  return [value];
}

/**
 * Split a single line of CSV into an array of strings.
 * @param {string} csvLine Supports optionally "quoted" strings which can contain commas.
 * @returns {string[]}
 */
export function splitCSV(csvLine) {
  // respect quotes
  let bits = [];
  let quoted = false;
  let bit = "";
  for (let i = 0; i < csvLine.length; i++) {
    const c = csvLine[i];
    if (bit === "" && c === " ") continue; // ignore leading spaces
    // Note: a bit must be all quoted, or all unquoted -- only leading quotes are used. Internal quotes are treated as part of the bit.
    if (c === '"') {
      if (!quoted) {
        if (bit === "") {
          quoted = true;
        } else {
          bit += c; // not initial quote, so add it as a character
        }
      } else {
        if (csvLine[i + 1] === '"') {
          // should we also handle \" ??
          bit += c; // escaped "" quote, so add it as a character, and skip the next quote
          i++;
        } else {
          // end of quoted bit
          quoted = false;
        }
      }
    } else if (c === "," && !quoted) {
      bits.push(bit);
      bit = "";
    } else {
      bit += c;
    }
  }
  bits.push(bit);
  return bits;
}

/**
 * Flexible translation function factory. Wraps i18next `t()`
 * Supports using human text as keys (via camelCase conversion).
 * Usage e.g.:
 * const t = i18n({ keyPrefix: "appHeader" });
 * keyPrefix is optional - if provided, it will be used as a possible prefix for the translation key
 * const text = t("Knowledge Base");
 */
export function i18n(options) {
  return function (text) {
    let key = toCamelCase(text);
    // try a couple of lookups
    let translationKeys = [options?.keyPrefix && `${options.keyPrefix}.${key}`, key];
    // apply t from i18next
    for (let translationKey of translationKeys) {
      let translated = t(translationKey);
      if (translated === translationKey) {
        // no translation - move on to next key
        continue;
      }
      return translated;
    }
    return text;
  };
}

export function toTitleCase(str) {
  if (!str) return str;
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

/**
 * Convert a variable or constant name to a more human-readable string.
 * @param {string?} str
 * @returns e.g. "questionGroup" -> "Question Group", or "ADD_FILE" -> "Add File"
 */
export function prettyPrintCodeName(str) {
  if (!str) return str;
  // UNDERLINE_SEPARATED constant names -> space separated
  str = str.replace(/_/g, " ");
  // ALL CAPS -> title case
  if (str.toUpperCase() === str) {
    return toTitleCase(str);
  }
  // camelCase -> space separated words
  str = str.replace(/(a-z)([A-Z])/g, "$1 $2");
  return toTitleCase(str);
}

export function prettyPrintLowMedianHighStatuses(value, category) {
  return STATUSFIELD_VALUE_LABEL[category]?.[value] ?? value;
}

export function camelToTitleCase(str) {
  if (!str) return "";

  // Convert camelCase to "Capitalized Words"
  return str
    .replace(/([A-Z])/g, " $1") // Add space before uppercase letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim(); // Remove extra spaces
}

/**
 * Convert a string to camel case.
 */
export function toCamelCase(str) {
  return str.replace(/\W/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Convenience for filter-nulls and join-with-spaces.
 * @param  {...any} args
 * @returns {!string} "" if no args
 */
export function space(...args) {
  if (!args) return "";
  return args.filter(Boolean).join(" ");
}

/**
 * Compare two strings ignoring case differences.
 * @param {string|null|undefined} str1 - First string to compare
 * @param {string|null|undefined} str2 - Second string to compare
 * @returns {boolean} true if strings are equal (ignoring case), false otherwise
 */
export function equalsIgnoreCase(str1, str2) {
  // Handle null/undefined cases
  if (str1 == null && str2 == null) return true;
  if (str1 == null || str2 == null) return false;

  // Convert to strings and compare in lowercase
  return String(str1).toLowerCase() === String(str2).toLowerCase();
}

/**
 * Check if an array contains a string, ignoring case differences.
 * @param {Array} array - Array to search in
 * @param {string|null|undefined} searchValue - Value to search for
 * @returns {boolean} true if array contains the value (ignoring case), false otherwise
 */
export function includesIgnoreCase(array, searchValue) {
  if (!Array.isArray(array)) return false;
  if (searchValue == null) return array.includes(searchValue);

  return array.some((item) => equalsIgnoreCase(item, searchValue));
}

/**
// To truncate the text if the length becomes longer.
* Check if an array contains a string, ignoring case differences.
 * @param {String} text - String to be shortened in length
 * @param {int} n - Length to compare to. (e.g., if the text's length is greater than 20 or 30 or 40 characters or so on).
 * @param {int} k - how many characters you want to keep in the string starting from the start.
 * @returns {String} reduced length string with '...' appeneded at the end if the length is more than 20 characters.
 * If you want to very the length of the string change the value of n
*/
export function shortenText(text, n, k) {
  const truncate = text?.length > n ? `${text.substring(0, k)}…` : text;
  return truncate;
}

/**
 * Convert markdown text to HTML.
 * Uses marked library.
 * If input is already html, it will be returned as-is.
 * @param markdown
 * @returns {string | Promise<string>}
 */
export function markdownToHtml(markdown) {
  return marked.parse(markdown, {breaks: true}); // returns HTML string
}

/**
 * Strip HTML tags from a string.
 * @param html
 * @returns {string|string}
 */
export function stripHtml(html) {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

/**
 * Copy text to clipboard, supporting both plain text and HTML.
 * Uses the Clipboard API.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/write
 * @param msg
 * @returns {Promise<void>}
 */
export const copyToClipboard = async (msg) => {
  const html = markdownToHtml(msg);
  const plainText = stripHtml(html); // or strip html to plain text

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plainText], { type: "text/plain" }),
      }),
    ]);
  } catch (err) {
    console.error("Failed to copy: ", err);
  }
};

